import React, { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import {
  FaUsers,
  FaBoxOpen,
  FaUserCog,
  FaCodeBranch,
  FaFileInvoiceDollar,
  FaReceipt,
} from "react-icons/fa";
import "../styles/Dashboard.css";
import Sidebar from "../components/Sidebar";
import { getData } from "../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../context/LoaderContext";
import Chart from "chart.js/auto";

const defaultCards = [
  {
    title: "Buyers",
    icon: <FaUsers size={32} />,
    value: "0",
    detail: "Active buyer accounts",
    slug: "buyers",
  },
  {
    title: "Branches",
    icon: <FaCodeBranch size={32} />,
    value: "0",
    detail: "Operational branches",
    slug: "sellers",
  },
  {
    title: "Products",
    icon: <FaBoxOpen size={32} />,
    value: "0",
    detail: "Published SKUs in catalog",
    slug: "products",
  },

  {
    title: "Users",
    icon: <FaUserCog size={32} />,
    value: "0",
    detail: "Internal team members",
    slug: "users",
  },
  {
    title: "Quotations",
    icon: <FaReceipt size={32} />,
    value: "0",
    detail: "Total quotations created",
    slug: "quotations",
  },
  {
    title: "Invoices",
    icon: <FaFileInvoiceDollar size={32} />,
    value: "0",
    detail: "Sales invoices issued",
    slug: "invoices",
  },
  {
    title: "FBR",
    icon: <FaFileInvoiceDollar size={32} />,
    value: "0",
    detail: "FBR synced invoices",
    slug: "fbr",
  },
];

const defaultMonthlySales = [];

export default function Dashboard() {
  const { showLoader, hideLoader } = useLoader();
  const [cards, setCards] = useState(defaultCards);
  const [monthlySales, setMonthlySales] = useState(defaultMonthlySales);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const formatMonthLabel = (monthValue, fallbackLabel) => {
    if (!monthValue || typeof monthValue !== "string") return fallbackLabel;
    const [year, month] = monthValue.split("-");
    const parsedMonth = Number(month);
    if (!Number.isFinite(parsedMonth)) return fallbackLabel;
    return `${new Date(0, parsedMonth - 1).toLocaleString("en-US", {
      month: "short",
    })} '${String(year).slice(-2)}`;
  };

  const normaliseMonthly = (series, fallback) => {
    if (!Array.isArray(series) || series.length === 0) return fallback;
    return series.map((item, index) => ({
      label: formatMonthLabel(item?.month, fallback?.[index]?.label),
      invoices: Number(item?.invoices) || 0,
      quotations: Number(item?.quotations) || 0,
      fbr: Number(item?.fbr) || 0,
    }));
  };

  const visibleMonths =
    monthlySales.filter(
      (m) => Number(m?.invoices) + Number(m?.quotations) + Number(m?.fbr) > 0
    ) || [];
  const chartData = visibleMonths.length ? visibleMonths : monthlySales;
  const hasChartData = chartData.length > 0;

  const slugAliases = {
    sellers: "branches",
    branch: "branches",
  };

  const applyDashboardPayload = (payload = {}) => {
    setCards((prevCards) =>
      prevCards.map((card) => {
        const lowerTitle = `${card.title || ""}`.toLowerCase();
        const alias =
          slugAliases[card.slug] || slugAliases[lowerTitle] || undefined;
        const counts = payload?.counts || {};
        const sales = payload?.sales || {};
        const bySlug =
          counts?.[card.slug] ??
          counts?.[alias] ??
          sales?.[card.slug] ??
          sales?.[alias];
        const byTitle = counts?.[lowerTitle] ?? sales?.[lowerTitle];
        const apiValue = bySlug ?? byTitle;
        if (apiValue === undefined || apiValue === null) return card;
        return { ...card, value: String(apiValue) };
      })
    );

    const monthly = normaliseMonthly(
      payload?.monthlySales || payload?.monthly_sales,
      defaultMonthlySales
    );

    setMonthlySales(monthly);
  };

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    if (!hasChartData) return;

    const labels = chartData.map((m) => m.label);
    const chart = new Chart(chartRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Invoices",
            data: chartData.map((m) => m.invoices || 0),
            backgroundColor: "rgba(14, 165, 233, 0.8)",
            borderRadius: 8,
            maxBarThickness: 24,
          },
          {
            label: "Quotations",
            data: chartData.map((m) => m.quotations || 0),
            backgroundColor: "rgba(244, 114, 182, 0.8)",
            borderRadius: 8,
            maxBarThickness: 24,
          },
          {
            label: "FBR",
            data: chartData.map((m) => m.fbr || 0),
            backgroundColor: "rgba(251, 146, 60, 0.85)",
            borderRadius: 8,
            maxBarThickness: 24,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 12 } },
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(148, 163, 184, 0.3)" },
            ticks: { precision: 0 },
          },
        },
        plugins: {
          legend: { position: "top" },
          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}` } },
        },
      },
    });

    chartInstanceRef.current = chart;

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, hasChartData]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        showLoader();
        const [dashboardResponse, monthlyResponse] = await Promise.all([
          getData("api/dashboard"),
          getData("api/sales/monthly"),
        ]);

        const dashboardPayload =
          dashboardResponse?.data && typeof dashboardResponse.data === "object"
            ? dashboardResponse.data
            : dashboardResponse;
        applyDashboardPayload(dashboardPayload);

        const monthlyPayload = Array.isArray(monthlyResponse?.data)
          ? monthlyResponse.data
          : monthlyResponse?.monthlySales ||
            monthlyResponse?.data?.monthlySales ||
            monthlyResponse;
        setMonthlySales(normaliseMonthly(monthlyPayload, defaultMonthlySales));
      } catch (error) {
        toast.error("Dashboard data not loaded.");
        console.error("Failed to fetch dashboard data", error);
      } finally {
        hideLoader();
      }
    };

    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <Navbar />

        <div className="dashboard-page">
          <main className="dashboard">
            <section className="cards-grid">
              {cards.map((item) => (
                <div
                  className={`dash-card dash-card-${item.slug}`}
                  key={item.title}
                >
                  <div className="dash-card-top">
                    <div className="dash-card-icon">{item.icon}</div>
                    <div className="dash-card-meta">
                      <p className="dash-card-title">{item.title}</p>
                      <h3 className="dash-card-value">{item.value}</h3>
                    </div>
                  </div>
                  <p className="dash-card-detail">{item.detail}</p>
                </div>
              ))}
            </section>

            <section className="chart-grid">
              <div className="chart-card">
                <div className="chart-card__header">
                  <div>
                    <p className="eyebrow">Monthly pipeline</p>
                    <h3>Invoices / Quotations / FBR</h3>
                  </div>
                </div>

                <div className="chartjs-wrapper">
                  {hasChartData ? (
                    <canvas ref={chartRef} />
                  ) : (
                    <div className="chart-empty">No monthly data to display.</div>
                  )}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
